import React, { useState, useEffect, useRef } from "react";
import Input from "./Input";
// import useClickOutside from "../../hooks/useClickOutside";
import Label from "./Label";
import ErrorMessage from "./ErrorMessage";
import { DropdownOptions, getLabelsForSelectBox } from "./MultiSelectBox";
import useClickOutside from "../../hooks/useClickOutside";
import MaterialIcon from "../BuilderComponents/MaterialIcon";

/**
 * A single-select box component with search functionality.
 *
 * This component allows users to select a single option from a dropdown list.
 * It supports search functionality to filter options and keyboard navigation for selection.
 *
 * @param {Array} options - Array of objects representing the selectable options.
 *                          Each object should have properties corresponding to the 'valueKey' and 'labelKey' values.
 * @param {string} valueKey - The key in the option objects that holds the value.
 * @param {string|Array} labelKey - The key(s) in the option objects that hold the label. Can be a single string or an array of strings.
 * @param {Object|string} selectedData - The initially selected data. Can be an object or a string (representing the valueKey).
 * @param {Function} onChange - Callback function to handle the selection change. Receives the selected option and the event as arguments.
 * @param {string} label - Optional label for the select box.
 * @param {string} placeholder - Optional placeholder for the select box.
 * @param {boolean} isSearch - Optional flag to enable search functionality.
 * @param {string} containerClassName - Optional className for the container wrapper of the component.
 * @param {object} containerStyle - Optional style object for the container wrapper.
 * @param {string} labelClassName - Optional className for the label element.
 * @param {string} error - Optional error message.
 * @param {string} errorMessageClassName - Optional className for the error message element.
 *
 * @example
 * <SingleSelectBox
 *   options={[
 *     { id: "1", name: "Item 1" },
 *     { id: "2", name: "Item 2" },
 *     { id: "3", name: "Item 3" }
 *   ]}
 *   valueKey="id"
 *   labelKey="name"
 *   selectedData={{ id: "2", name: "Item 2" }}  // Using an object
 *   selectedData="2" // Using a string
 *   onChange={(selectedOption, event) => console.log(selectedOption)}
 *   label="Select an Item"
 *   isSearch={true}
 * />
 */

const SingleSelectBox = ({
  options,
  valueKey = "id",
  labelKey = ["name"],
  selectedData,
  onChange,
  label,
  placeholder = "select an option",
  isSearch = false,
  isApiSearch = false,
  apiCallback, // Add apiCallback prop
  containerClassName = "",
  containerStyle = {},
  labelClassName = "",
  error = "",
  errorMessageClassName = ""
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useClickOutside(dropdownRef, () => setIsDropdownOpen(false));

  useEffect(() => {
    let initialSelectedOption = null;

    if (typeof selectedData === "object" && selectedData !== null) {
      initialSelectedOption = selectedData;
    } else if (typeof selectedData === "string") {
      if (Array.isArray(options) && typeof options[0] === "string") {
        initialSelectedOption = options.includes(selectedData) ? selectedData : null;
      } else {
        initialSelectedOption = options.find((option) => option[valueKey] === selectedData);
      }
    }

    setSelectedOption(initialSelectedOption);
  }, [selectedData, options, valueKey]);

  const handleOptionClick = (option, event) => {
    setSelectedOption(option);
    onChange(option, event);
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  useEffect(() => {
    if (isApiSearch) {
      apiCallback(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, isApiSearch]);

  // Filter options only if isApiSearch is false
  const filteredOptions = isApiSearch
    ? options
    : options.filter((option) => {
        const labels = Array.isArray(labelKey)
          ? labelKey.map((key) => option[key] || "").join(" ")
          : option[labelKey] || "";

        return labels.toLowerCase().includes(searchTerm.toLowerCase());
      });

  const clearSelection = (e) => {
    e.stopPropagation();
    setSelectedOption(null);
    if (typeof selectedOption === "string") {
      onChange("");
    } else {
      onChange({ id: "", name: "" });
    }
  };

  const containerClasses = `single_custom_select_box ${containerClassName}`.trim();
  return (
    <div className={containerClasses} style={containerStyle} ref={dropdownRef}>
      <SingleSelectArea
        selectedOption={selectedOption}
        searchTerm={searchTerm}
        onDropdownClick={() => setIsDropdownOpen(!isDropdownOpen)}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        labelKey={labelKey}
        placeholder={placeholder}
        onClearSelection={clearSelection}
        isSearch={isSearch}
        isDropdownOpen={isDropdownOpen}
        labelClassName={labelClassName}
        label={label}
        valueKey={valueKey}
      />
      {isDropdownOpen && (
        <DropdownOptions
          options={filteredOptions}
          labelKey={labelKey}
          onOptionClick={(option, e) => handleOptionClick(option, e)}
        />
      )}
      {error && <ErrorMessage className={errorMessageClassName}>{error}</ErrorMessage>}
    </div>
  );
};

export default SingleSelectBox;

const SingleSelectArea = ({
  selectedOption,
  searchTerm,
  onDropdownClick,
  onSearchChange,
  labelKey,
  delimiter = ", ",
  placeholder,
  isSearch,
  isDropdownOpen,
  onClearSelection,
  labelClassName,
  label
  // valueKey
  // onKeyDown
}) => {
  return (
    <div className="form_element_wrapper_text">
      {label && <Label className={labelClassName}>{label}</Label>}
      <div className="single_selected_items" onClick={onDropdownClick}>
        {isSearch ? (
          <Input
            type="text"
            placeholder={selectedOption ? getLabelsForSelectBox(selectedOption, labelKey, delimiter) : placeholder}
            value={searchTerm}
            inputClassName={selectedOption ? "select_search_placeholder" : ""}
            onChange={onSearchChange}
            readOnly={isDropdownOpen ? false : true}
            // inputBoxStyle={{ height: "36px" }}
            // onKeyDown={onKeyDown} // Add keydown event here
          />
        ) : (
          <Input
            type="text"
            placeholder={placeholder}
            value={selectedOption ? getLabelsForSelectBox(selectedOption, labelKey, delimiter) : ""}
            readOnly
          />
        )}

        <div className="single_dropdown_arrow">
          {selectedOption && (
            <MaterialIcon
              name="close"
              className="close_icon font_size_medium"
              onClick={onClearSelection}
              style={{ color: "#667085" }}
            />
          )}
          {isDropdownOpen ? (
            <MaterialIcon name="keyboard_arrow_down" style={{ color: "#667085" }} />
          ) : (
            <MaterialIcon name="keyboard_arrow_down" className="arrow_drop_right" style={{ color: "#667085" }} />
          )}
        </div>
      </div>
    </div>
  );
};
